import { boxShadowLevel1 } from '@issuu/ixu';
import {
    colorBaseGreyWarm50,
    colorBaseGreyWarm100,
    colorBaseWhite,
    layoutGutterSmall,
    layoutMarginSmall,
    ProductButton as Button,
    ProductHeading as Heading,
    ProductParagraph as Paragraph,
} from '@issuu/silkscreen';
import type { ArticleStorySummary as Summary } from 'product-document-page-shared';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { breakpointWidths, isSafariIOS, ReaderMessageType } from '../../constants';
import DocumentContext from '../DocumentPage/DocumentContext';
import { formatArticleStoryPageNumbers } from '../shared/pages-number';
import { LineClamp } from '../shared/styles';

const Wrapper = styled.div`
    position: relative;
`;

const StoryLink = styled.a`
    display: grid;
    gap: ${layoutGutterSmall};
    align-items: center;
    grid-template-columns: auto 1fr;
    border-bottom: 0.5px solid ${colorBaseGreyWarm100};
    padding-block: ${layoutMarginSmall};
    background-color: ${colorBaseGreyWarm50};
    /* The below rules are allowed as an exception as this is a block element
         with a well defined functionality - Begin */
    color: inherit;
    text-decoration: none;
    /* End */
`;

const ImageHover = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${colorBaseWhite};
    text-transform: uppercase;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.15s;
    &:hover {
        opacity: 1;
    }
`;

const ImageContainer = styled.div`
    display: flex;
    align-items: flex-start;
    justify-items: stretch;
    grid-area: span 4;
    position: relative;
    width: 13rem;
    height: 9.75rem;
    border-radius: 0.5rem;
    box-shadow: ${boxShadowLevel1};
    transition: box-shadow 0.15s;
    font-weight: bold;
    overflow: hidden;

    @media (max-width: ${breakpointWidths.smallMax}px) {
        width: 7.5rem;
        height: 6rem;
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-image: linear-gradient(
            -90deg,
            hsla(0, 0%, 74%, 0) 4%,
            hsla(0, 0%, 100%, 0.4) 15%,
            hsla(0, 0%, 100%, 0.2) 39%
        );
        opacity: 0.77;
        pointer-events: none;
    }
`;

const CoverImage = styled.img.attrs({ alt: '', role: 'presentation' })`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
`;

const Uppercase = styled.span`
    text-transform: uppercase;
`;

const PageButtonWrapper = styled.div`
    position: absolute;
    bottom: ${layoutMarginSmall};
    left: calc(13rem + ${layoutGutterSmall});

    @media (max-width: ${breakpointWidths.smallMax}px) {
        left: calc(7.5rem + ${layoutGutterSmall});
    }
`;

export type Props = {
    articleStorySummary: Summary;
};

export const ArticleStorySummary: React.FC<Props> = (props: Props) => {
    const { articleStorySummary } = props;

    const {
        document: { documentName },
        publisher: { username },
        trackers,
        appState: { readerRef, sendMessageToReader },
    } = useContext(DocumentContext);

    const gotoPageInReader = useCallback(() => {
        if (readerRef.current) {
            const ref = readerRef.current;
            ref.scrollIntoView({ behavior: 'smooth' });
            setTimeout(() => {
                ref.setAttribute('tabindex', '-1');
                ref.focus();
                ref.removeAttribute('tabindex');
                sendMessageToReader.current &&
                    sendMessageToReader.current(
                        ReaderMessageType.goToPage,
                        articleStorySummary.pageNumbers[0],
                    );
            }, 300);
        }
    }, [readerRef, sendMessageToReader]);

    const openArticleStory = useCallback(() => {
        gotoPageInReader();
        trackers.website({
            type: 'story_click',
            story_id: `${articleStorySummary.id}`,
        });
        if (isSafariIOS) {
            window.open(`/${username}/docs/${documentName}/s/${articleStorySummary.id}`);
        } else {
            window.setTimeout(
                () => window.open(`/${username}/docs/${documentName}/s/${articleStorySummary.id}`),
                1000,
            );
        }
    }, [gotoPageInReader]);

    const [viewRef, inView] = useInView();
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        if (!seen) {
            trackers.website({
                type: 'story_impression',
                story_id: `${articleStorySummary.id}`,
            });
            setSeen(true);
        }
    }, [inView]);

    return (
        <Wrapper>
            <StoryLink
                href={`/${username}/docs/${documentName}/s/${articleStorySummary.id}`}
                onClick={event => {
                    event.preventDefault();
                    openArticleStory();
                }}
                ref={viewRef}
            >
                <ImageContainer>
                    <CoverImage src={articleStorySummary.coverImageUrl} />
                    <ImageHover aria-hidden="true">Read story</ImageHover>
                </ImageContainer>
                <Paragraph step="negative-2">
                    <Uppercase>{articleStorySummary.minutesToRead} min read</Uppercase>
                </Paragraph>
                <LineClamp lines={2}>
                    <Heading level={3} step="negative-1">
                        {articleStorySummary.title}
                    </Heading>
                </LineClamp>

                <LineClamp lines={3}>
                    <Paragraph step="negative-2">{articleStorySummary.body}</Paragraph>
                </LineClamp>
            </StoryLink>
            <PageButtonWrapper>
                <Paragraph step="negative-2">
                    <Button onClick={gotoPageInReader} role="link" size="small" variant="ghost">
                        {formatArticleStoryPageNumbers(articleStorySummary.pageNumbers)}
                    </Button>
                </Paragraph>
            </PageButtonWrapper>
        </Wrapper>
    );
};
